import React, {
  createContext, useContext, useState, useCallback, ReactNode, useMemo, useEffect,
} from 'react';
import { configurationService } from '../../QnAConfigModal/services';
import { ErrorMessageBuilder } from '../../QnAConfigModal/services/ErrorMessageBuilder';
import { storageService } from '../../QnAConfigModal/services/StorageService';
import { useAIServiceManager } from '../hooks/useAIServiceManager';
import { useContextManager } from '../hooks/useContextManager';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useMessageManager } from '../hooks/useMessageManager';
import { useNotifications } from '../hooks/useNotifications';
import { useRetryLogic } from '../hooks/useRetryLogic';
import { QnAWidgetState, ChatMessage } from '../types';

interface QnAContextType extends QnAWidgetState {
  toggleExpanded: () => void;
  sendMessage: (content: string) => Promise<boolean>;
  retryLastAction: () => void;
  dismissError: () => void;
  clearChatHistory: () => void;
  updateContext: (newContext: string) => Promise<void>;
  showNotification: (message: string, type?: 'success' | 'info' | 'warning', autoHide?: boolean, timeout?: number) => void;
  dismissNotification: () => void;
  refreshAIConfiguration: () => void;
}

interface QnAContextProviderProps {
  children: ReactNode;
}

const QnAContext = createContext<QnAContextType | undefined>(undefined);

export function QnAContextProvider({ children }: QnAContextProviderProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxContextMessages, setMaxContextMessages] = useState<number>(20);

  const { notification, showNotification, dismissNotification } = useNotifications();
  const {
    messages, addMessage, updateMessage, clearMessages, getMessagesForAI,
  } = useMessageManager(maxContextMessages);
  const { context, updateContext } = useContextManager(storageService);
  const {
    aiServiceManager, isAIBusy, refreshAIConfiguration, sendMessageToAI,
  } = useAIServiceManager();
  const errorHandler = useErrorHandler();

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const config = await configurationService.getConfig();
        setMaxContextMessages(config.maxContextMessages);
      } catch (error) {
        setMaxContextMessages(20);
      }
    };

    loadConfiguration();
  }, [aiServiceManager]);

  // Note: reloadContext is not available anymore in useContextManager,
  // this might need adjustment if retry logic for context is required.
  const { retryLastAction } = useRetryLogic({
    errorHandler,
    getMessagesForAI,
    sendMessageToAI,
    updateMessage,
    addMessage,
    context,
  });

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content,
      timestamp: new Date(),
    };

    addMessage(userMessage);
    errorHandler.clearError();

    try {
      const aiMessages = getMessagesForAI();
      aiMessages.push({ role: 'user', content });

      const aiMessageId = `ai-${Date.now()}`;
      const placeholderAiMessage: ChatMessage = {
        id: aiMessageId,
        type: 'ai',
        content: '',
        timestamp: new Date(),
      };

      addMessage(placeholderAiMessage);

      await sendMessageToAI(
        aiMessages,
        context || undefined,
        updateMessage,
        aiMessageId,
      );

      return true;
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: `system-${Date.now()}`,
        type: 'system',
        content: 'Failed to get AI response. Please try again.',
        timestamp: new Date(),
      };

      const userFriendlyError = ErrorMessageBuilder.buildUserFriendlyMessage(error);

      addMessage(errorMessage);
      errorHandler.setError(userFriendlyError);
      errorHandler.setFailedMessage(content);
      errorHandler.setFailedAction('message');
      errorHandler.setSuccessCallback(null);

      return false;
    }
  }, [addMessage, getMessagesForAI, context, sendMessageToAI, updateMessage, errorHandler]);

  const dismissError = useCallback(() => {
    errorHandler.clearError();
  }, [errorHandler]);

  const clearChatHistory = useCallback(() => {
    clearMessages();
    showNotification('Chat history cleared', 'info');
  }, [clearMessages, showNotification]);

  const value: QnAContextType = useMemo(() => ({
    isExpanded,
    messages,
    isAIBusy,
    context,
    contextLoading: false, // This is now local to the config tab
    error: errorHandler.error,
    isRetrying: errorHandler.isRetrying,
    lastFailedMessage: errorHandler.lastFailedMessage,
    lastFailedAction: errorHandler.lastFailedAction,
    lastSuccessCallback: errorHandler.lastSuccessCallback,
    notification,
    toggleExpanded,
    sendMessage,
    retryLastAction,
    dismissError,
    clearChatHistory,
    updateContext,
    showNotification,
    dismissNotification,
    refreshAIConfiguration,
  }), [
    isExpanded,
    messages,
    isAIBusy,
    context,
    errorHandler.error,
    errorHandler.isRetrying,
    errorHandler.lastFailedMessage,
    errorHandler.lastFailedAction,
    errorHandler.lastSuccessCallback,
    notification,
    toggleExpanded,
    sendMessage,
    retryLastAction,
    dismissError,
    clearChatHistory,
    updateContext,
    showNotification,
    dismissNotification,
    refreshAIConfiguration,
  ]);

  return (
    <QnAContext.Provider value={value}>
      {children}
    </QnAContext.Provider>
  );
}

export const useQnAContext = (): QnAContextType => {
  const context = useContext(QnAContext);
  if (context === undefined) {
    throw new Error('useQnAContext must be used within a QnAContextProvider');
  }
  return context;
};
